import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListOrdered, Plus, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@apollo/client';
import Layout from '@/components/Layout';
import { QueueFormDialog } from '@/components/publishQueues/QueueFormDialog';
import { PublishQueueCard } from '@/components/publishQueues/PublishQueueCard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type PublishQueueFieldsFragment,
  useCreateMyPublishQueueMutation,
  useDeleteMyPublishQueueMutation,
  useMyPublishQueuesQuery,
  useMySocialConnectionsQuery,
  useUpdateMyPublishQueueMutation,
} from '@/generated/graphql';
import type { QueueDraft } from '@/lib/publishQueues/types';
import {
  selectQueueScheduledPosts,
  type ScheduledPublishRow,
} from '@/lib/publishQueues/selectQueueScheduledPosts';
import { QUEUE_SCHEDULED_PUBLISHES } from '@/queries/publishQueues';
import { isPublishPlatform } from '@/lib/publishing/types';

export const PublishQueuesPage = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQueue, setEditingQueue] = useState<PublishQueueFieldsFragment | null>(null);
  const [queueToDelete, setQueueToDelete] = useState<PublishQueueFieldsFragment | null>(null);

  const {
    data,
    loading,
    error,
    refetch,
  } = useMyPublishQueuesQuery({ fetchPolicy: 'cache-and-network' });
  const { data: connectionData } = useMySocialConnectionsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [createQueue, { loading: creating }] = useCreateMyPublishQueueMutation();
  const [updateQueue, { loading: updating }] = useUpdateMyPublishQueueMutation();
  const [deleteQueue, { loading: deleting }] = useDeleteMyPublishQueueMutation();
  // Queues book at most a few weeks ahead; 90 days covers every realistic backlog.
  const [scheduleWindow] = useState(() => {
    const now = new Date();
    return { now, from: now.toISOString(), to: new Date(now.getTime() + 90 * 86_400_000).toISOString() };
  });
  const {
    data: scheduledData,
    loading: scheduledLoading,
    error: scheduledError,
  } = useQuery<{ myScheduledPublishes: ScheduledPublishRow[] }>(QUEUE_SCHEDULED_PUBLISHES, {
    variables: { from: scheduleWindow.from, to: scheduleWindow.to },
    fetchPolicy: 'cache-and-network',
  });

  const queues = data?.myPublishQueues ?? [];
  const connections = useMemo(
    () => (connectionData?.mySocialConnections ?? []).filter(
      (connection) => connection.status === 'active' && isPublishPlatform(connection.platform),
    ),
    [connectionData?.mySocialConnections],
  );
  const connectionsById = useMemo(
    () => new Map(connections.map((connection) => [connection.id, connection])),
    [connections],
  );

  const openCreate = () => {
    setEditingQueue(null);
    setDialogOpen(true);
  };

  const openEdit = (queue: PublishQueueFieldsFragment) => {
    setEditingQueue(queue);
    setDialogOpen(true);
  };

  const handleSave = async (draft: QueueDraft) => {
    const input = {
      name: draft.name.trim(),
      weeklySlots: draft.weeklySlots,
      socialConnectionIds: draft.socialConnectionIds,
      multiAccountMode: draft.multiAccountMode,
      minGapMinutes: draft.minGapMinutes,
      timezone: draft.timezone.trim(),
      spacingScope: draft.spacingScope,
    };
    try {
      if (editingQueue) {
        await updateQueue({ variables: { id: editingQueue.id, input } });
        toast.success(t('publishQueues.toasts.updated'));
      } else {
        await createQueue({ variables: { input } });
        toast.success(t('publishQueues.toasts.created'));
      }
      setDialogOpen(false);
      await refetch();
    } catch {
      toast.error(t('publishQueues.toasts.saveError'));
    }
  };

  const handleDelete = async () => {
    if (!queueToDelete) return;
    try {
      await deleteQueue({ variables: { id: queueToDelete.id } });
      toast.success(t('publishQueues.toasts.deleted'));
      setQueueToDelete(null);
      await refetch();
    } catch {
      toast.error(t('publishQueues.toasts.deleteError'));
    }
  };

  return (
    <Layout>
      <div className="w-full min-w-0 space-y-6 p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-primary">
              <ListOrdered className="h-4 w-4" aria-hidden="true" />
              {t('publishQueues.eyebrow')}
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{t('publishQueues.title')}</h1>
            <p className="mt-1 max-w-2xl text-muted-foreground">{t('publishQueues.subtitle')}</p>
          </div>
          <Button type="button" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t('publishQueues.create')}
          </Button>
        </div>

        {error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <p className="text-sm text-destructive">{t('publishQueues.loadError')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => void refetch()}
            >
              {t('publishQueues.retry')}
            </Button>
          </div>
        ) : null}

        {loading && queues.length === 0 ? (
          <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-64 rounded-xl" />
            ))}
          </div>
        ) : null}

        {!loading && !error && queues.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex min-h-72 flex-col items-center justify-center p-8 text-center">
              <div className="mb-4 rounded-full bg-primary/10 p-4 text-primary">
                <Radio className="h-7 w-7" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-semibold">{t('publishQueues.empty.title')}</h2>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                {t('publishQueues.empty.description')}
              </p>
              <Button type="button" className="mt-5" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                {t('publishQueues.empty.cta')}
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {queues.length > 0 ? (
          <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {queues.map((queue) => (
              <PublishQueueCard key={queue.id} queue={queue}
                connections={queue.social_connection_ids.flatMap((id) => {
                  const connection = connectionsById.get(id);
                  return connection ? [connection] : [];
                })}
                upcoming={{
                  posts: selectQueueScheduledPosts({
                    queue,
                    publishes: scheduledData?.myScheduledPublishes ?? [],
                    now: scheduleWindow.now,
                  }),
                  loading: scheduledLoading,
                  error: Boolean(scheduledError),
                }}
                onEdit={() => openEdit(queue)} onDelete={() => setQueueToDelete(queue)} />
            ))}
          </div>
        ) : null}
      </div>

      <QueueFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        connections={connections}
        initialQueue={editingQueue}
        submitting={creating || updating}
        onSubmit={handleSave}
      />

      <AlertDialog
        open={queueToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setQueueToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('publishQueues.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('publishQueues.deleteDialog.description', { name: queueToDelete?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('publishQueues.deleteDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction disabled={deleting} onClick={() => void handleDelete()}>
              {t(deleting ? 'publishQueues.deleteDialog.deleting' : 'publishQueues.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default PublishQueuesPage;
